import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { UserTenantRoleSortFields, SortDirection, GetUserTenantRolesQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/userTenantRole';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useUserTenantRolesTable = ({ queryOptions }: { queryOptions?: GetUserTenantRolesQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetUserTenantRolesQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as UserTenantRoleSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
                  withUser: true,
                  withTenant: true,
                  withRole: true,
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: userTenantRoleData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetUserTenantRolesQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      userTenantRoleData
        ? { totalCount: userTenantRoleData.userTenantRoles.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [userTenantRoleData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type UserTenantRolesBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function UserTenantRolesBaseTable(props: UserTenantRolesBaseTableProps) {
  const tableProps = useUserTenantRolesTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
